import { useState, useEffect } from 'react';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

export default function ComputerVisionPage() {
  const [activeTab, setActiveTab] = useState('manufacturing');
  const [showConsultationModal, setShowConsultationModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    company: '',
    job_title: '',
    email: '',
    phone: '',
    industry: '',
    company_size: '',
    interests: [] as string[],
    project_description: '',
    timeline: '',
    privacy_consent: false,
  });
  const [charCount, setCharCount] = useState(0);
  const [submitAddr, setSubmitAddr] = useState('');

  useEffect(() => {
    const fetchFormUrl = async () => {
      try {
        const response = await fetch('https://readdy.ai/api/public/form-url', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: 'Computer Vision Consultation Request',
          }),
        });
        const data = await response.json();
        if (data.submitAddr) {
          setSubmitAddr(data.submitAddr);
        }
      } catch (error) {
        console.error('Error fetching form URL:', error);
      }
    };

    fetchFormUrl();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox' && (e.target as HTMLInputElement).checked !== undefined) {
      const checked = (e.target as HTMLInputElement).checked;
      if (name === 'privacy_consent') {
        setFormData(prev => ({ ...prev, [name]: checked }));
      } else if (name === 'interests') {
        const checkboxValue = (e.target as HTMLInputElement).value;
        setFormData(prev => ({
          ...prev,
          interests: checked
            ? [...prev.interests, checkboxValue]
            : prev.interests.filter(item => item !== checkboxValue)
        }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
      
      if (name === 'project_description') {
        setCharCount(value.length);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!submitAddr) {
      alert('Form configuration error. Please try again later.');
      return;
    }

    if (formData.project_description.length > 1000) {
      alert('Project description must not exceed 1000 characters.');
      return;
    }

    const formDataToSubmit = new URLSearchParams();
    formDataToSubmit.append('first_name', formData.first_name);
    formDataToSubmit.append('last_name', formData.last_name);
    formDataToSubmit.append('company', formData.company);
    formDataToSubmit.append('job_title', formData.job_title);
    formDataToSubmit.append('email', formData.email);
    formDataToSubmit.append('phone', formData.phone);
    formDataToSubmit.append('industry', formData.industry);
    formDataToSubmit.append('company_size', formData.company_size);
    formDataToSubmit.append('interests', formData.interests.join(', '));
    formDataToSubmit.append('project_description', formData.project_description);
    formDataToSubmit.append('timeline', formData.timeline);
    formDataToSubmit.append('privacy_consent', formData.privacy_consent ? 'Yes' : 'No');

    try {
      const response = await fetch(submitAddr, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSubmit.toString(),
      });

      if (response.ok) {
        setShowConsultationModal(false);
        setShowSuccessModal(true);
        setFormData({
          first_name: '',
          last_name: '',
          company: '',
          job_title: '',
          email: '',
          phone: '',
          industry: '',
          company_size: '',
          interests: [],
          project_description: '',
          timeline: '',
          privacy_consent: false,
        });
        setCharCount(0);
      } else {
        alert('Failed to submit form. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('An error occurred. Please try again.');
    }
  };

  const switchTab = (tab: string) => {
    setActiveTab(tab);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative w-full bg-cover bg-center" style={{ backgroundImage: 'url(https://public.readdy.ai/ai/img_res/6d14e280855c3c1ecd3ece94d577d431.jpg)' }}>
        <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/80 to-transparent"></div>
        <div className="container mx-auto px-4 py-20 relative">
          <div className="w-full max-w-2xl">
            <div className="mb-4">
              <span className="inline-block bg-teal-500/10 text-teal-600 px-3 py-1 rounded-full text-sm font-medium">
                AI-Powered Computer Vision
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Transform Your Business with Intelligent Computer Vision Solutions
            </h1>
            <p className="text-lg text-gray-700 mb-8">
              Harness the power of AI-driven computer vision to automate processes, enhance security, improve quality control, and unlock new insights from visual data across your enterprise.
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => setShowConsultationModal(true)}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg whitespace-nowrap font-medium inline-flex items-center hover:bg-blue-700 transition-colors cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-message-3-line"></i>
                </div>
                Get Free Consultation
              </button>
              <button className="border border-gray-300 text-gray-700 px-6 py-3 rounded-lg whitespace-nowrap font-medium inline-flex items-center hover:bg-gray-50 transition-colors cursor-pointer">
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-play-circle-line"></i>
                </div>
                Watch Demo
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities Overview */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Computer Vision Capabilities Overview</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Our advanced computer vision platform leverages cutting-edge AI algorithms to analyze, understand, and extract meaningful insights from visual data in real-time.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 text-center transition-all hover:shadow-md">
              <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="w-8 h-8 flex items-center justify-center text-blue-600">
                  <i className="ri-eye-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">Object Detection & Recognition</h3>
              <p className="text-gray-600 mb-4">
                Identify and classify objects, people, vehicles, and products with 99.5% accuracy using advanced neural networks.
              </p>
              <div className="text-sm text-gray-500">
                <div className="flex items-center justify-center mb-1">
                  <div className="w-4 h-4 flex items-center justify-center mr-1">
                    <i className="ri-check-line text-green-500"></i>
                  </div>
                  Real-time processing
                </div>
                <div className="flex items-center justify-center">
                  <div className="w-4 h-4 flex items-center justify-center mr-1">
                    <i className="ri-check-line text-green-500"></i>
                  </div>
                  Multi-object tracking
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 text-center transition-all hover:shadow-md">
              <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="w-8 h-8 flex items-center justify-center text-green-600">
                  <i className="ri-image-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">Image Classification & Analysis</h3>
              <p className="text-gray-600 mb-4">
                Automatically categorize and analyze images for content moderation, quality assessment, and data organization.
              </p>
              <div className="text-sm text-gray-500">
                <div className="flex items-center justify-center mb-1">
                  <div className="w-4 h-4 flex items-center justify-center mr-1">
                    <i className="ri-check-line text-green-500"></i>
                  </div>
                  Custom model training
                </div>
                <div className="flex items-center justify-center">
                  <div className="w-4 h-4 flex items-center justify-center mr-1">
                    <i className="ri-check-line text-green-500"></i>
                  </div>
                  Batch processing
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 text-center transition-all hover:shadow-md">
              <div className="w-16 h-16 bg-orange-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="w-8 h-8 flex items-center justify-center text-orange-600">
                  <i className="ri-shield-check-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">Automated Quality Control</h3>
              <p className="text-gray-600 mb-4">
                Detect defects, anomalies, and quality issues in manufacturing processes with precision and speed.
              </p>
              <div className="text-sm text-gray-500">
                <div className="flex items-center justify-center mb-1">
                  <div className="w-4 h-4 flex items-center justify-center mr-1">
                    <i className="ri-check-line text-green-500"></i>
                  </div>
                  Defect detection
                </div>
                <div className="flex items-center justify-center">
                  <div className="w-4 h-4 flex items-center justify-center mr-1">
                    <i className="ri-check-line text-green-500"></i>
                  </div>
                  Anomaly identification
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 text-center transition-all hover:shadow-md">
              <div className="w-16 h-16 bg-purple-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="w-8 h-8 flex items-center justify-center text-purple-600">
                  <i className="ri-user-face-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">Facial Recognition Systems</h3>
              <p className="text-gray-600 mb-4">
                Secure access control, attendance tracking, and identity verification with advanced facial recognition technology.
              </p>
              <div className="text-sm text-gray-500">
                <div className="flex items-center justify-center mb-1">
                  <div className="w-4 h-4 flex items-center justify-center mr-1">
                    <i className="ri-check-line text-green-500"></i>
                  </div>
                  Liveness detection
                </div>
                <div className="flex items-center justify-center">
                  <div className="w-4 h-4 flex items-center justify-center mr-1">
                    <i className="ri-check-line text-green-500"></i>
                  </div>
                  Privacy compliant
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Applications */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Industry Applications</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Our computer vision solutions are transforming operations across diverse industries, delivering measurable ROI and competitive advantages.
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
            <div className="flex border-b border-gray-100">
              <button
                className={`px-6 py-4 font-medium transition-colors ${
                  activeTab === 'manufacturing'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-blue-600'
                }`}
                onClick={() => switchTab('manufacturing')}
              >
                Manufacturing
              </button>
              <button
                className={`px-6 py-4 font-medium transition-colors ${
                  activeTab === 'retail'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-blue-600'
                }`}
                onClick={() => switchTab('retail')}
              >
                Retail
              </button>
              <button
                className={`px-6 py-4 font-medium transition-colors ${
                  activeTab === 'healthcare'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-blue-600'
                }`}
                onClick={() => switchTab('healthcare')}
              >
                Healthcare
              </button>
              <button
                className={`px-6 py-4 font-medium transition-colors ${
                  activeTab === 'security'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-blue-600'
                }`}
                onClick={() => switchTab('security')}
              >
                Security
              </button>
            </div>
            <div className="p-8">
              {activeTab === 'manufacturing' && (
                <div className="flex flex-col lg:flex-row items-center gap-8">
                  <div className="w-full lg:w-1/2">
                    <h3 className="text-2xl font-bold mb-4">Manufacturing Excellence</h3>
                    <p className="text-gray-600 mb-6">
                      Enhance production quality, reduce defects, and optimize manufacturing processes with AI-powered visual inspection systems.
                    </p>
                    <div className="space-y-4">
                      <div className="flex items-start">
                        <div className="w-6 h-6 bg-blue-500/10 rounded-full flex items-center justify-center mr-3 mt-1">
                          <div className="w-4 h-4 flex items-center justify-center text-blue-600">
                            <i className="ri-check-line"></i>
                          </div>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-1">Automated Quality Inspection</h4>
                          <p className="text-gray-600 text-sm">
                            Real-time defect detection with 99.8% accuracy, reducing manual inspection time by 85%.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <div className="w-6 h-6 bg-blue-500/10 rounded-full flex items-center justify-center mr-3 mt-1">
                          <div className="w-4 h-4 flex items-center justify-center text-blue-600">
                            <i className="ri-check-line"></i>
                          </div>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-1">Assembly Line Monitoring</h4>
                          <p className="text-gray-600 text-sm">
                            Track production progress and identify bottlenecks in real-time for optimal efficiency.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <div className="w-6 h-6 bg-blue-500/10 rounded-full flex items-center justify-center mr-3 mt-1">
                          <div className="w-4 h-4 flex items-center justify-center text-blue-600">
                            <i className="ri-check-line"></i>
                          </div>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-1">Predictive Maintenance</h4>
                          <p className="text-gray-600 text-sm">
                            Visual analysis of equipment condition to predict maintenance needs and prevent downtime.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="w-full lg:w-1/2">
                    <div className="rounded-lg overflow-hidden">
                      <img
                        src="https://readdy.ai/api/search-image?query=Modern%20automated%20manufacturing%20assembly%20line%20with%20AI%20computer%20vision%20quality%20control%20system%20robotic%20inspection%20cameras%20monitoring%20production%20with%20digital%20overlay%20showing%20defect%20detection%20and%20quality%20metrics%20in%20clean%20industrial%20facility%20with%20blue%20lighting%20and%20advanced%20technology&width=800&height=600&seq=cv-mfg-001&orientation=landscape"
                        alt="Manufacturing Excellence"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'retail' && (
                <div className="flex flex-col lg:flex-row items-center gap-8">
                  <div className="w-full lg:w-1/2">
                    <h3 className="text-2xl font-bold mb-4">Retail Intelligence</h3>
                    <p className="text-gray-600 mb-6">
                      Transform customer experience and optimize operations with intelligent visual analytics for retail environments.
                    </p>
                    <div className="space-y-4">
                      <div className="flex items-start">
                        <div className="w-6 h-6 bg-blue-500/10 rounded-full flex items-center justify-center mr-3 mt-1">
                          <div className="w-4 h-4 flex items-center justify-center text-blue-600">
                            <i className="ri-check-line"></i>
                          </div>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-1">Inventory Management</h4>
                          <p className="text-gray-600 text-sm">
                            Automated stock monitoring and shelf analysis to optimize inventory levels and reduce stockouts.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <div className="w-6 h-6 bg-blue-500/10 rounded-full flex items-center justify-center mr-3 mt-1">
                          <div className="w-4 h-4 flex items-center justify-center text-blue-600">
                            <i className="ri-check-line"></i>
                          </div>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-1">Customer Analytics</h4>
                          <p className="text-gray-600 text-sm">
                            Understand customer behavior, foot traffic patterns, and demographic insights for better decision-making.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <div className="w-6 h-6 bg-blue-500/10 rounded-full flex items-center justify-center mr-3 mt-1">
                          <div className="w-4 h-4 flex items-center justify-center text-blue-600">
                            <i className="ri-check-line"></i>
                          </div>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-1">Loss Prevention</h4>
                          <p className="text-gray-600 text-sm">
                            Advanced theft detection and suspicious behavior identification to reduce shrinkage.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="w-full lg:w-1/2">
                    <div className="rounded-lg overflow-hidden">
                      <img
                        src="https://readdy.ai/api/search-image?query=Modern%20retail%20store%20with%20AI%20computer%20vision%20technology%20smart%20cameras%20monitoring%20customer%20behavior%20and%20inventory%20digital%20analytics%20overlay%20showing%20foot%20traffic%20patterns%20and%20product%20tracking%20in%20bright%20contemporary%20shopping%20environment%20with%20clean%20minimalist%20design&width=800&height=600&seq=cv-retail-001&orientation=landscape"
                        alt="Retail Intelligence"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'healthcare' && (
                <div className="flex flex-col lg:flex-row items-center gap-8">
                  <div className="w-full lg:w-1/2">
                    <h3 className="text-2xl font-bold mb-4">Healthcare Innovation</h3>
                    <p className="text-gray-600 mb-6">
                      Advance medical diagnostics and patient care with AI-powered medical imaging and analysis solutions.
                    </p>
                    <div className="space-y-4">
                      <div className="flex items-start">
                        <div className="w-6 h-6 bg-blue-500/10 rounded-full flex items-center justify-center mr-3 mt-1">
                          <div className="w-4 h-4 flex items-center justify-center text-blue-600">
                            <i className="ri-check-line"></i>
                          </div>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-1">Medical Image Analysis</h4>
                          <p className="text-gray-600 text-sm">
                            AI-assisted radiology and pathology analysis for faster, more accurate diagnoses.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <div className="w-6 h-6 bg-blue-500/10 rounded-full flex items-center justify-center mr-3 mt-1">
                          <div className="w-4 h-4 flex items-center justify-center text-blue-600">
                            <i className="ri-check-line"></i>
                          </div>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-1">Patient Monitoring</h4>
                          <p className="text-gray-600 text-sm">
                            Contactless vital sign monitoring and fall detection for enhanced patient safety.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <div className="w-6 h-6 bg-blue-500/10 rounded-full flex items-center justify-center mr-3 mt-1">
                          <div className="w-4 h-4 flex items-center justify-center text-blue-600">
                            <i className="ri-check-line"></i>
                          </div>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-1">Surgical Assistance</h4>
                          <p className="text-gray-600 text-sm">
                            Real-time surgical guidance and instrument tracking for improved precision and outcomes.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="w-full lg:w-1/2">
                    <div className="rounded-lg overflow-hidden">
                      <img
                        src="https://readdy.ai/api/search-image?query=Advanced%20medical%20imaging%20technology%20with%20AI%20computer%20vision%20analysis%20doctor%20reviewing%20diagnostic%20scans%20on%20digital%20screens%20with%20neural%20network%20overlay%20highlighting%20medical%20findings%20in%20modern%20healthcare%20facility%20with%20clean%20professional%20environment%20and%20blue%20medical%20lighting&width=800&height=600&seq=cv-health-001&orientation=landscape"
                        alt="Healthcare Innovation"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'security' && (
                <div className="flex flex-col lg:flex-row items-center gap-8">
                  <div className="w-full lg:w-1/2">
                    <h3 className="text-2xl font-bold mb-4">Security & Surveillance</h3>
                    <p className="text-gray-600 mb-6">
                      Enhance security operations with intelligent video analytics, threat detection, and automated response systems.
                    </p>
                    <div className="space-y-4">
                      <div className="flex items-start">
                        <div className="w-6 h-6 bg-blue-500/10 rounded-full flex items-center justify-center mr-3 mt-1">
                          <div className="w-4 h-4 flex items-center justify-center text-blue-600">
                            <i className="ri-check-line"></i>
                          </div>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-1">Perimeter Security</h4>
                          <p className="text-gray-600 text-sm">
                            Advanced intrusion detection with real-time alerts and automated threat assessment.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <div className="w-6 h-6 bg-blue-500/10 rounded-full flex items-center justify-center mr-3 mt-1">
                          <div className="w-4 h-4 flex items-center justify-center text-blue-600">
                            <i className="ri-check-line"></i>
                          </div>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-1">Access Control</h4>
                          <p className="text-gray-600 text-sm">
                            Biometric authentication and facial recognition for secure facility access management.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <div className="w-6 h-6 bg-blue-500/10 rounded-full flex items-center justify-center mr-3 mt-1">
                          <div className="w-4 h-4 flex items-center justify-center text-blue-600">
                            <i className="ri-check-line"></i>
                          </div>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-1">Behavioral Analysis</h4>
                          <p className="text-gray-600 text-sm">
                            Detect suspicious activities and anomalous behavior patterns for proactive security measures.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="w-full lg:w-1/2">
                    <div className="rounded-lg overflow-hidden">
                      <img
                        src="https://readdy.ai/api/search-image?query=Advanced%20security%20surveillance%20system%20with%20AI%20computer%20vision%20technology%20multiple%20camera%20feeds%20on%20monitoring%20screens%20showing%20facial%20recognition%20and%20threat%20detection%20in%20modern%20security%20control%20room%20with%20blue%20ambient%20lighting%20and%20professional%20setup&width=800&height=600&seq=cv-security-001&orientation=landscape"
                        alt="Security & Surveillance"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Service Offerings */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Our Service Offerings</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              From consultation to deployment and ongoing support, we provide comprehensive computer vision services tailored to your business needs.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100 transition-all hover:shadow-md">
              <div className="w-16 h-16 bg-blue-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-blue-600">
                  <i className="ri-code-box-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-4">Custom Development</h3>
              <p className="text-gray-600 mb-6">
                Tailored computer vision solutions built specifically for your unique business requirements and workflows.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-green-500 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700 text-sm">Requirements analysis & consultation</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-green-500 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700 text-sm">Custom model development & training</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-green-500 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700 text-sm">API development & documentation</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-green-500 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700 text-sm">Performance optimization</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100 transition-all hover:shadow-md">
              <div className="w-16 h-16 bg-green-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-green-600">
                  <i className="ri-settings-3-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-4">System Integration</h3>
              <p className="text-gray-600 mb-6">
                Seamlessly integrate computer vision capabilities into your existing infrastructure and business processes.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-green-500 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700 text-sm">Legacy system integration</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-green-500 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700 text-sm">Cloud & edge deployment</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-green-500 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700 text-sm">Real-time data processing</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-green-500 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700 text-sm">Scalable architecture design</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100 transition-all hover:shadow-md">
              <div className="w-16 h-16 bg-orange-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-orange-600">
                  <i className="ri-graduation-cap-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-4">Training & Support</h3>
              <p className="text-gray-600 mb-6">
                Comprehensive training programs and ongoing support to ensure successful adoption and optimal performance.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-green-500 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700 text-sm">Technical team training</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-green-500 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700 text-sm">User documentation & guides</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-green-500 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700 text-sm">24/7 technical support</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-green-500 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700 text-sm">Maintenance & updates</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Consultation Form Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Request a Consultation</h2>
              <p className="text-gray-600">
                Ready to explore how computer vision can transform your business? Fill out the form below and our experts will contact you within 24 hours to discuss your specific requirements.
              </p>
            </div>
            <form className="space-y-6" onSubmit={handleSubmit} data-readdy-form>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-1">
                    First Name *
                  </label>
                  <input
                    id="first_name"
                    name="first_name"
                    required
                    value={formData.first_name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                    placeholder="Enter your first name"
                  />
                </div>
                <div>
                  <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name *
                  </label>
                  <input
                    id="last_name"
                    name="last_name"
                    required
                    value={formData.last_name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                    placeholder="Enter your last name"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name *
                  </label>
                  <input
                    id="company"
                    name="company"
                    required
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                    placeholder="Enter your company name"
                  />
                </div>
                <div>
                  <label htmlFor="job_title" className="block text-sm font-medium text-gray-700 mb-1">
                    Job Title *
                  </label>
                  <input
                    id="job_title"
                    name="job_title"
                    required
                    value={formData.job_title}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                    placeholder="Enter your job title"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Business Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                    placeholder="Enter your business email"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                    placeholder="Enter your phone number"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="industry" className="block text-sm font-medium text-gray-700 mb-1">
                    Industry *
                  </label>
                  <div className="relative">
                    <select
                      id="industry"
                      name="industry"
                      required
                      value={formData.industry}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm appearance-none pr-8"
                    >
                      <option value="">Select your industry</option>
                      <option value="manufacturing">Manufacturing</option>
                      <option value="retail">Retail & E-commerce</option>
                      <option value="healthcare">Healthcare</option>
                      <option value="security">Security & Surveillance</option>
                      <option value="automotive">Automotive</option>
                      <option value="logistics">Logistics & Transportation</option>
                      <option value="agriculture">Agriculture</option>
                      <option value="other">Other</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <div className="w-4 h-4 flex items-center justify-center text-gray-400">
                        <i className="ri-arrow-down-s-line"></i>
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <label htmlFor="company_size" className="block text-sm font-medium text-gray-700 mb-1">
                    Company Size *
                  </label>
                  <div className="relative">
                    <select
                      id="company_size"
                      name="company_size"
                      required
                      value={formData.company_size}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm appearance-none pr-8"
                    >
                      <option value="">Select company size</option>
                      <option value="1-10">1-10 employees</option>
                      <option value="11-50">11-50 employees</option>
                      <option value="51-200">51-200 employees</option>
                      <option value="201-500">201-500 employees</option>
                      <option value="501-1000">501-1000 employees</option>
                      <option value="1000+">1000+ employees</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <div className="w-4 h-4 flex items-center justify-center text-gray-400">
                        <i className="ri-arrow-down-s-line"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Areas of Interest *</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      name="interests"
                      value="object_detection"
                      checked={formData.interests.includes('object_detection')}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 mr-3"
                    />
                    <span className="text-sm text-gray-700">Object Detection & Recognition</span>
                  </label>
                  <label className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      name="interests"
                      value="quality_control"
                      checked={formData.interests.includes('quality_control')}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 mr-3"
                    />
                    <span className="text-sm text-gray-700">Automated Quality Control</span>
                  </label>
                  <label className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      name="interests"
                      value="facial_recognition"
                      checked={formData.interests.includes('facial_recognition')}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 mr-3"
                    />
                    <span className="text-sm text-gray-700">Facial Recognition Systems</span>
                  </label>
                  <label className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      name="interests"
                      value="image_analysis"
                      checked={formData.interests.includes('image_analysis')}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 mr-3"
                    />
                    <span className="text-sm text-gray-700">Image Classification & Analysis</span>
                  </label>
                  <label className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      name="interests"
                      value="surveillance"
                      checked={formData.interests.includes('surveillance')}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 mr-3"
                    />
                    <span className="text-sm text-gray-700">Security & Surveillance</span>
                  </label>
                  <label className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      name="interests"
                      value="custom_solution"
                      checked={formData.interests.includes('custom_solution')}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 mr-3"
                    />
                    <span className="text-sm text-gray-700">Custom Solution Development</span>
                  </label>
                </div>
              </div>

              <div>
                <label htmlFor="project_description" className="block text-sm font-medium text-gray-700 mb-1">
                  Project Description
                </label>
                <textarea
                  id="project_description"
                  name="project_description"
                  rows={4}
                  value={formData.project_description}
                  onChange={handleInputChange}
                  maxLength={1000}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                  placeholder="Tell us about your computer vision requirements, current challenges, and goals..."
                ></textarea>
                <div className="mt-1 text-sm text-gray-500 flex items-center">
                  <div className="w-4 h-4 flex items-center justify-center mr-1">
                    <i className="ri-information-line"></i>
                  </div>
                  <span>{charCount}</span>/1000 characters
                </div>
              </div>

              <div>
                <label htmlFor="timeline" className="block text-sm font-medium text-gray-700 mb-1">
                  Project Timeline
                </label>
                <div className="relative">
                  <select
                    id="timeline"
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm appearance-none pr-8"
                  >
                    <option value="">Select timeline</option>
                    <option value="immediate">Immediate (within 1 month)</option>
                    <option value="short">Short-term (1-3 months)</option>
                    <option value="medium">Medium-term (3-6 months)</option>
                    <option value="long">Long-term (6+ months)</option>
                    <option value="exploring">Just exploring options</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <div className="w-4 h-4 flex items-center justify-center text-gray-400">
                      <i className="ri-arrow-down-s-line"></i>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="privacy_consent"
                  name="privacy_consent"
                  required
                  checked={formData.privacy_consent}
                  onChange={handleInputChange}
                  className="w-4 h-4 mt-1 text-teal-600 border-gray-300 rounded focus:ring-teal-500 mr-3"
                />
                <label htmlFor="privacy_consent" className="text-sm text-gray-600">
                  I agree to the processing of my data according to the Privacy Policy and consent to being contacted by Thyamis regarding computer vision solutions. *
                </label>
              </div>

              <div className="text-center">
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-8 py-3 rounded-lg whitespace-nowrap font-medium hover:bg-blue-700 transition-colors inline-flex items-center cursor-pointer"
                >
                  <div className="w-5 h-5 flex items-center justify-center mr-2">
                    <i className="ri-send-plane-line"></i>
                  </div>
                  Request Consultation
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      <Footer />

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowSuccessModal(false)}></div>
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 relative">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="w-8 h-8 flex items-center justify-center text-green-500">
                  <i className="ri-check-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-2">Thank You!</h3>
              <p className="text-gray-600 mb-6">
                Your consultation request has been submitted successfully. Our computer vision experts will contact you within 24 hours.
              </p>
              <button
                onClick={() => setShowSuccessModal(false)}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg whitespace-nowrap font-medium hover:bg-blue-700 transition-colors cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
